import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaCheckCircle,
  FaUserCircle,
  FaArrowLeft,
  FaArrowRight,
  FaPaperPlane,
} from "react-icons/fa";
import { MdAccessTime } from "react-icons/md";
import "./Exam.css";
import axios from "axios";
import { Circles } from "react-loader-spinner";
import { toast } from "react-toastify";

const Exam = () => {
  const [timeLeft, setTimeLeft] = useState(1800);
  const [answers, setAnswers] = useState({});
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [activeSet, setActiveSet] = useState("set1");
  const [activePassageIndex, setActivePassageIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const token = localStorage.getItem("token");
  const [user, setUser] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [peraQuestions, setPeraQuestions] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!id || !token) {
      localStorage.removeItem("token");
      navigate("/");
    }
  }, [id, token]);

  const formatTime = () => {
    const min = Math.floor(timeLeft / 60);
    const sec = timeLeft % 60;
    return `${min}:${sec < 10 ? "0" : ""}${sec}`;
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  useEffect(() => {
    const fetchCandidate = async () => {
      try {
        const response = await axios.get(
          "https://app.instaconnects.com/api/v1/exams/candidate/details",
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response?.status === 200) {
          setUser(response?.data.data);
        }
      } catch (error) {
        console.log("Candidate fetch error", error);
      }
    };
    fetchCandidate();
  }, [token]);

  useEffect(() => {
    if (!user) return;
    const fetchQuestions = async () => {
      try {
        const response = await axios.get(
          "https://app.instaconnects.com/api/v1/exams/getRandomeQuestions",
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.status === 200) {
          setQuestions(response?.data?.data);
        }
      } catch (error) {
        console.log("Set 1 fetch error", error);
      }
    };
    fetchQuestions();
  }, [user, token]);

  useEffect(() => {
    if (!user) return;
    const fetchPeraQuestions = async () => {
      try {
        const response = await axios.get(
          "https://app.instaconnects.com/api/v1/exams/getRandomePeraQuestions",
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.status === 200) {
          setPeraQuestions(response?.data?.data);
        }
      } catch (error) {
        console.log("Set 2 fetch error", error);
      }
    };
    fetchPeraQuestions();
  }, [user, token]);

  const handleAnswer = (qid, value) => {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
  };

  const answeredCount = Object.keys(answers).length;

  const calculateScore = () => {
    let newScore = 0;
    if (!questions) return 0;
    questions.forEach((question) => {
      const userAnswer = answers[question._id];
      if (userAnswer && userAnswer === question.correctAnswer) {
        newScore++;
      }
    });
    return newScore;
  };

const handleSubmit = async () => {
  if (activeSet === "set2") {
    const set2Answered = peraQuestions?.some((p) =>
      p.questions.some((q) => answers[q._id]?.trim())
    );
    if (!set2Answered) {
      toast.error("Please answer at least one passage question before submitting.");
      return;
    }
  }

  setLoading(true);
  const finalScore = calculateScore();

  const attemptedAnswers = questions
    ?.filter((q) => answers[q._id])
    .map((q) => ({
      questionId: q._id,
      selectedOption: answers[q._id],
      isCorrect: answers[q._id] === q.correctAnswer,
    }));

const paragraphAnswers =
  peraQuestions?.flatMap((p) =>
    p.questions
      .map((q) => {
        const answerText = (answers[q._id] || "").trim();
        if (!answerText) return null; 
        return {
          questionId: q._id,
          answerText,
          expectedAnswer: q.correctAnswer || "",
        };
      })
      .filter(Boolean) 
  ) || [];

  try {
    const response = await axios.post(
      `https://app.instaconnects.com/api/v1/exams/endExam/${id}`,
      {
        result: finalScore,
        totalQuestions: questions?.length || 0,
        attemptQuestions: answeredCount,
        attemptedAnswers: attemptedAnswers || [],
        paragraphAnswers: paragraphAnswers,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response?.status === 200) {
      navigate("/thankyou");
      localStorage.removeItem("token");
      toast.success("Exam submitted successfully!");
      setLoading(false);
    } else {
      toast.error("Exam submission failed. Try again.");
      setLoading(false);
    }
  } catch (error) {
    toast.error("Submission error. Please try again.");
    setLoading(false);
  }
};


  if (!questions || !peraQuestions) {
    return (
      <div className="loaderContainer">
        <Circles height="60" width="60" color="#ff6162" visible />
        <span>Loading questions...</span>
      </div>
    );
  }

  return (
    <div className="exam-wrapper">
      <aside className="sidebar">
        <div className="logo">
          <img
            src="https://hradminpanel.instaconnects.com/assets/newLogo-Qx8rvfwM.png"
            alt="Logo"
          />
        </div>

        <div className="user-info">
          <p>
            <FaUserCircle size={24} />
            <strong>{user?.name?.toUpperCase() || "Guest"}</strong>
          </p>
          <p className="email">{user?.email?.toLowerCase() || "NA"}</p>
          <p className="email">{user?.position?.toUpperCase() || "NA"}</p>
        </div>

        <div className="timer">
          <MdAccessTime size={20} />
          <span>{formatTime()}</span>
        </div>

        <div className="question-navigator">
          <h4>Navigate Questions</h4>
          <div className="question-buttons">
            {activeSet === "set1" &&
              questions.map((q, index) => (
                <button
                  key={q._id}
                  className={`nav-button ${answers[q._id] ? "answered" : ""} ${
                    index === currentQIndex ? "active" : ""
                  }`}
                  onClick={() => setCurrentQIndex(index)}
                >
                  {index + 1} {answers[q._id] && <FaCheckCircle />}
                </button>
              ))}

            {activeSet === "set2" &&
              peraQuestions.map((_, index) => (
                <button
                  key={index}
                  className={`nav-button ${
                    index === activePassageIndex ? "active" : ""
                  }`}
                  onClick={() => setActivePassageIndex(index)}
                >
                  {index + 1}
                </button>
              ))}
          </div>
        </div>

        <div className="status-summary">
          {activeSet === "set1" ? (
            <>
              <p>
                <FaCheckCircle color="green" /> Answered:{" "}
                {
                  questions.filter(
                    (q) => answers[q._id] !== undefined && answers[q._id] !== ""
                  ).length
                }
              </p>
              <p>
                Remaining:{" "}
                {questions.length -
                  questions.filter(
                    (q) => answers[q._id] !== undefined && answers[q._id] !== ""
                  ).length}
              </p>
            </>
          ) : (
            <>
              <p>
                <FaCheckCircle color="green" /> Answered:{" "}
                {
                  peraQuestions
                    .flatMap((p) => p.questions)
                    .filter(
                      (q) => answers[q._id] && answers[q._id].trim() !== ""
                    ).length
                }
              </p>
              <p>
                Remaining:{" "}
                {peraQuestions.flatMap((p) => p.questions).length -
                  peraQuestions
                    .flatMap((p) => p.questions)
                    .filter(
                      (q) => answers[q._id] && answers[q._id].trim() !== ""
                    ).length}
              </p>
            </>
          )}
        </div>

        <button
          className="submit-button-sidebar"
          onClick={() => setShowModal(true)}
        >
          <FaPaperPlane /> Submit Exam
        </button>
      </aside>

      <main className="exam-content">
        <div className="marquee-container">
          <p className="marquee-text">
            ⚠️ Please do not reload or close this window until submission is
            complete...
          </p>
        </div>

        <div className="exam-titleContainer">
          <h2 className="exam-title">
            Assessment In Progress <span>{formatTime()}</span>
          </h2>
          <div className="exam-titleSetCon">
            <h2
              className={activeSet === "set1" ? "active" : ""}
              onClick={() => setActiveSet("set1")}
            >
              Set 1
            </h2>
            <h2
              className={activeSet === "set2" ? "active" : ""}
              onClick={() => setActiveSet("set2")}
            >
              Set 2
            </h2>
          </div>
        </div>

        {activeSet === "set1" && (
          <form
            className="exam-form"
            onSubmit={(e) => {
              e.preventDefault();
              setShowModal(true);
            }}
          >
            <div className="question-block">
              <p className="question-text">
                {currentQIndex + 1}. {questions[currentQIndex].question}
              </p>
              <div className="options-group">
                {questions[currentQIndex].options.map((opt, i) => (
                  <label className="option-label" key={i}>
                    <input
                      type="radio"
                      name={questions[currentQIndex]._id}
                      value={opt}
                      checked={answers[questions[currentQIndex]._id] === opt}
                      onChange={() =>
                        handleAnswer(questions[currentQIndex]._id, opt)
                      }
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </div>
            <div className="navigation-buttons">
              <button
                type="button"
                onClick={() => setCurrentQIndex(Math.max(currentQIndex - 1, 0))}
                disabled={currentQIndex === 0}
              >
                <FaArrowLeft /> Previous
              </button>
              <button
                type="button"
                onClick={() =>
                  setCurrentQIndex(
                    Math.min(currentQIndex + 1, questions.length - 1)
                  )
                }
                disabled={currentQIndex === questions.length - 1}
              >
                Next <FaArrowRight />
              </button>
            </div>
          </form>
        )}

        {activeSet === "set2" && (
          <div className="pera-section">
            {peraQuestions[activePassageIndex] && (
              <div className="pera-block">
                <p className="passage-text">
                  <strong>Passage {activePassageIndex + 1}:</strong>{" "}
                  {peraQuestions[activePassageIndex].passage}
                </p>
                {peraQuestions[activePassageIndex].questions.map((q, idx) => (
                  <div key={q._id} className="pera-question">
                    <label className="ParaQuestions">
                      {idx + 1}. {q.question}
                    </label>
                    <textarea
                      rows={3}
                      value={answers[q._id] || ""}
                      onChange={(e) => handleAnswer(q._id, e.target.value)}
                      placeholder="Type your answer here..."
                    />
                  </div>
                ))}
              </div>
            )}
            <div className="navigation-buttons">
              <button
                type="button"
                onClick={() =>
                  setActivePassageIndex((prev) => Math.max(prev - 1, 0))
                }
                disabled={activePassageIndex === 0}
              >
                <FaArrowLeft /> Previous
              </button>
              <button
                type="button"
                onClick={() =>
                  setActivePassageIndex((prev) =>
                    Math.min(prev + 1, peraQuestions.length - 1)
                  )
                }
                disabled={activePassageIndex === peraQuestions.length - 1}
              >
                Next <FaArrowRight />
              </button>
            </div>
          </div>
        )}
      </main>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Confirm Submission</h3>
            <p>Are you sure you want to submit your exam?</p>
            <div className="modal-buttons">
              <button onClick={handleSubmit} className="confirm-button">
                <FaCheckCircle /> {loading ? "Please Wait..." : "Yes, Submit"}
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="cancel-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Exam;
